import React, { useState, useEffect } from 'react';
import AdminNavbar from '../components/AdminNavbar';
import { fetchNews, createNews, updateNews, deleteNews } from '../utils/newsApi';
import './AdminGallery.css';

export default function AdminNews() {
    const [newsItems, setNewsItems] = useState([]);
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [summary, setSummary] = useState('');
    
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const loadNews = async () => {
        const data = await fetchNews();
        setNewsItems(data || []);
    };

    useEffect(() => {
        loadNews();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!title || !date || !summary) {
            setMessage({ type: 'danger', text: 'Please fill in all fields.' });
            return;
        }

        try {
            setLoading(true);
            setMessage({ type: '', text: editingId ? 'Updating news item...' : 'Saving news item...' });
            
            if (editingId) {
                await updateNews(editingId, { title, date, summary });
                setMessage({ type: 'success', text: 'Success! News item updated.' });
                setEditingId(null);
            } else {
                await createNews({ title, date, summary });
                setMessage({ type: 'success', text: 'Success! News item added.' });
            }
            
            setTitle('');
            setDate('');
            setSummary('');
            loadNews();
        } catch (error) {
            setMessage({ type: 'danger', text: `Failed: ${error.message}` });
        } finally {
            setLoading(false);
        }
    }

    const handleEdit = (item) => {
        setEditingId(item.id);
        setTitle(item.title || '');
        setDate(item.date || '');
        setSummary(item.summary || '');
    }

    const cancelEdit = () => {
        setEditingId(null);
        setTitle('');
        setDate('');
        setSummary('');
    }

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this news item?")) return;
        
        try {
            await deleteNews(id);
            setMessage({ type: 'success', text: 'News item deleted!' });
            loadNews();
        } catch(error) {
            setMessage({ type: 'danger', text: 'Failed to delete news.' });
        }
    }

    return (
        <div className="admin-gallery-container pt-5 pb-5">
            <div className="container">
                <AdminNavbar />

                {message.text && (
                    <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                        {message.text}
                        <button type="button" className="close" onClick={() => setMessage({type: '', text: ''})} aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                )}

                <div className="row">
                    {/* Create Section */}
                    <div className="col-lg-4 mb-4">
                        <div className="admin-card upload-card">
                            <h4>{editingId ? 'Edit News & Events' : 'Create News & Events'}</h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>{editingId ? 'Update this news entry.' : 'Add a new update for the home page.'}</p>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group mt-3">
                                    <label>Title</label>
                                    <input 
                                        type="text" 
                                        className="form-control admin-input" 
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="e.g. Welcome Back to School!"
                                        required
                                    />
                                </div>
                                <div className="form-group mt-3">
                                    <label>Date</label>
                                    <input 
                                        type="text" 
                                        className="form-control admin-input" 
                                        value={date}
                                        onChange={(e) => setDate(e.target.value)}
                                        placeholder="e.g. Sep 15, 2024"
                                        required
                                    />
                                </div>
                                <div className="form-group mt-3">
                                    <label>Summary</label>
                                    <textarea 
                                        className="form-control admin-input" 
                                        value={summary}
                                        onChange={(e) => setSummary(e.target.value)}
                                        placeholder="Enter a brief summary..."
                                        rows="4"
                                        required
                                    />
                                </div>

                                <button type="submit" className="upload-btn w-100 mt-4" disabled={loading}>
                                    {loading ? 'Saving...' : editingId ? 'Update News Item' : 'Add News Item'}
                                </button>
                                {editingId && (
                                    <button type="button" onClick={cancelEdit} className="btn btn-secondary w-100 mt-2 font-weight-bold" disabled={loading}>
                                        Cancel Edit
                                    </button>
                                )}
                            </form>
                        </div>
                    </div>

                    {/* News Grid Section */}
                    <div className="col-lg-8">
                        <div className="admin-card">
                            <h4>Current News & Events</h4>
                            <p className="text-muted" style={{fontSize: '0.9rem'}}>Manage existing updates shown on the home page.</p>
                            
                            <div className="row admin-grid mt-4">
                                {newsItems.length > 0 ? newsItems.map((item) => (
                                    <div className="col-md-6 mb-4" key={item.id}>
                                        <div className="card shadow-sm border-0 h-100">
                                            <div className="card-body">
                                                <div className="d-flex align-items-center mb-2">
                                                    <i className="fa fa-calendar-alt text-secondary mr-2"></i>
                                                    <small className="text-muted font-weight-bold">{item.date}</small>
                                                </div>
                                                <h5 className="card-title text-primary mb-2">{item.title}</h5>
                                                <p className="card-text text-muted" style={{fontSize: '0.9rem'}}>{item.summary}</p>
                                                
                                                <div className="d-flex justify-content-between mt-3" style={{gap: '10px'}}>
                                                    <button onClick={() => handleEdit(item)} className="btn btn-sm btn-outline-primary flex-fill font-weight-bold">
                                                        ✏️ Edit
                                                    </button>
                                                    <button onClick={() => handleDelete(item.id)} className="btn btn-sm btn-outline-danger flex-fill font-weight-bold">
                                                        🗑️ Delete
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="col-12 text-center py-5">
                                        <h5 className="text-muted">No news events found. Create one!</h5>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
